import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

function Orb() {
    const svgRef = useRef(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/data/geography.json`)
            .then(response => response.json())
            .then(jsonData => setData(jsonData));
    }, []);

    useEffect(() => {
        if (!data) return;

        const width = 1200;
        const height = 900;
        const margin = { top: 20, right: 120, bottom: 20, left: 120 };

        d3.select(svgRef.current).selectAll("*").remove();

        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height);

        const g = svg.append('g');

        // Add zoom behavior
        const zoom = d3.zoom()
            .scaleExtent([0.1, 3])
            .on('zoom', (event) => {
                g.attr('transform', event.transform);
            });

        svg.call(zoom);

        const groupedData = d3.group(data, d => d.continent);

        const hierarchicalData = {
            name: "Orb",
            children: Array.from(groupedData, ([continent, countries]) => ({
                name: continent,
                children: countries.map(c => ({ name: c.country }))
            }))
        };

        const treeLayout = d3.tree()
            .size([height - margin.top - margin.bottom, width - margin.left - margin.right])
            .separation((a, b) => (a.parent == b.parent ? 1 : 2) / a.depth);

        const root = d3.hierarchy(hierarchicalData);

        root.descendants().forEach(d => {
            if (d.depth === 1) {
                d._children = d.children;
                d.children = null;
            }
        });

        update(root);

        function update(source) {
            const treeData = treeLayout(root);
            const nodes = treeData.descendants();
            const links = treeData.links();

            nodes.forEach((d, i) => {
                d.y = d.depth * 250; // Increased horizontal spacing
                if (d.parent) {
                    const siblings = d.parent.children || d.parent._children;
                    const index = siblings.indexOf(d);
                    d.x = d.x + (index - (siblings.length - 1) / 2) * 20; // Adjusted vertical spacing
                }
            });

            const node = g.selectAll('g.node')
                .data(nodes, d => d.id || (d.id = d.data.name));

            const nodeEnter = node.enter().append('g')
                .attr('class', 'node')
                .attr('transform', d => `translate(${source.y0 || 0},${source.x0 || 0})`)
                .on('click', (event, d) => {
                    if (d.children) {
                        d._children = d.children;
                        d.children = null;
                    } else {
                        d.children = d._children;
                        d._children = null;
                    }
                    update(d);
                });

            nodeEnter.append('circle')
                .attr('r', 10)
                .style('fill', d => (d._children ? '#ffcc00' : '#fff'))
                .style('stroke', '#000');

            nodeEnter.append('text')
                .attr('dy', '.35em')
                .attr('x', d => (d.children || d._children ? -13 : 13))
                .attr('text-anchor', d => (d.children || d._children ? 'end' : 'start'))
                .text(d => d.data.name);

            const nodeUpdate = node.merge(nodeEnter).transition().duration(750)
                .attr('transform', d => `translate(${d.y},${d.x})`);

            nodeUpdate.select('circle')
                .style('fill', d => (d._children ? '#ffcc00' : '#fff'));

            const nodeExit = node.exit().transition().duration(750)
                .attr('transform', d => `translate(${source.y},${source.x})`)
                .remove();

            nodeExit.select('circle')
                .attr('r', 1e-6);

            nodeExit.select('text')
                .style('fill-opacity', 1e-6);

            const link = g.selectAll('path.link')
                .data(links, d => d.target.id);

            const linkGenerator = d3.linkHorizontal()
                .x(d => d.y)
                .y(d => d.x);

            const customLink = (d) => {
                const path = linkGenerator(d);
                const midX = (d.source.y + d.target.y) / 2;
                return `${path.split('C')[0]}C${midX},${d.source.x} ${midX},${d.target.x} ${d.target.y},${d.target.x}`;
            };

            const linkEnter = link.enter().insert('path', 'g')
                .attr('class', 'link')
                .attr('d', customLink)
                .style('fill', 'none')
                .style('stroke', '#ccc')
                .style('stroke-width', '2px');

            link.merge(linkEnter).transition().duration(750)
                .attr('d', customLink);

            link.exit().transition().duration(750)
                .attr('d', customLink)
                .remove();

            nodes.forEach(d => {
                d.x0 = d.x;
                d.y0 = d.y;
            });
        }
    }, [data]);

    return (
        <div className="header-container">
            <h2>World Countries Collapsible Tree</h2>
            <svg ref={svgRef}></svg>
        </div>
    );
}

export default Orb;